import {
  ClickAwayListener,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  SxProps,
  Theme,
  Typography,
} from '@mui/material';
import { ReactNode, useRef, useState } from 'react';
import { DynamicIcon } from 'src/components/DynamicIcon/dynamic-icon';
import { Pill } from 'src/components/Pill';
import { Tooltip } from 'src/components/Tooltip/Tooltip';
import {
  participantMenuHelpTextSx,
  participantMenuItemContentSx,
  participantMenuItemSx,
  participantPillIconSx,
} from './participant-pill.styles';
import { useHoverCardDelay } from './use-hover-card-delay';

export type ParticipantPillKind = 'agent' | 'client' | 'rsm' | 'unknown';

export interface ParticipantPillMenuItem {
  icon?: ReactNode;
  label: string;
  onClick: () => void;
}

export interface ParticipantPillProps {
  kind: ParticipantPillKind;
  label: string;
  title?: string;
  isFollower?: boolean;
  readOnly?: boolean;
  menuItems?: ParticipantPillMenuItem[];
  menuHelpText?: string;
  hoverCard?: ReactNode;
}

const KIND_ICON_NAME: Record<ParticipantPillKind, string> = {
  agent: 'headphones',
  client: 'user',
  rsm: 'radar',
  unknown: 'message-circle-question',
};

const KIND_DEFAULT_TITLE: Record<ParticipantPillKind, string> = {
  agent: 'Agent — click for options',
  client: 'Client',
  rsm: 'Remote systems monitor',
  unknown: 'This sender is not linked to any client yet',
};

const KIND_DEFAULT_TITLE_READ_ONLY: Record<ParticipantPillKind, string> = {
  ...KIND_DEFAULT_TITLE,
  agent: 'Agent',
};

const FOLLOWER_TITLE = 'Follower — click for options';
const FOLLOWER_TITLE_READ_ONLY = 'Follower';
const OPTIONS_ACTION_LABEL = 'Options';

const getDefaultPillTitle = (
  kind: ParticipantPillKind,
  isFollower: boolean,
  readOnly: boolean,
): string => {
  if (kind === 'agent' && isFollower) {
    return readOnly ? FOLLOWER_TITLE_READ_ONLY : FOLLOWER_TITLE;
  }

  return readOnly ? KIND_DEFAULT_TITLE_READ_ONLY[kind] : KIND_DEFAULT_TITLE[kind];
};

// Theme's MuiTooltip styleOverrides add their own border/shadow/background; strip
// them so ClientHoverCard is the only layer drawing chrome. Must go through
// slotProps (styled(Tooltip)'s className lands on the child, not the portaled popper).
const BARE_TOOLTIP_SLOT_SX: SxProps<Theme> = {
  padding: 0,
  margin: '0.25rem 0 0',
  backgroundColor: 'transparent',
  border: 'none',
  boxShadow: 'none',
  maxWidth: 'none',
};

export const ParticipantPill = ({
  kind,
  label,
  title,
  isFollower = false,
  readOnly = false,
  menuItems,
  menuHelpText,
  hoverCard,
}: ParticipantPillProps) => {
  const pillRef = useRef<HTMLButtonElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const {
    isOpen: hoverCardOpen,
    openNow: openHoverCardNow,
    closeNow: closeHoverCardNow,
    openSoon: openHoverCardSoon,
    closeSoon: closeHoverCardSoon,
    toggle: toggleHoverCard,
  } = useHoverCardDelay();

  const hasMenu = !readOnly && !!menuItems?.length;
  const hasHoverCard = !!hoverCard;
  const hasAction = hasMenu || hasHoverCard;
  const showFollowerBell = kind === 'agent' && isFollower;
  const hasOverlay = hasMenu && (kind === 'agent' || kind === 'unknown');
  const resolvedTitle = title ?? getDefaultPillTitle(kind, isFollower, readOnly);

  const openMenu = () => setMenuOpen(true);

  const handleClick = () => {
    if (hasHoverCard) {
      toggleHoverCard();
      return;
    }
    if (hasMenu) {
      openMenu();
    }
  };

  const pillButton = (
    <Pill
      ref={pillRef}
      variant={kind === 'unknown' ? 'dashed' : 'solid'}
      label={label}
      icon={
        <DynamicIcon
          name={KIND_ICON_NAME[kind]}
          library="lucide"
          size={13}
          sx={participantPillIconSx(kind)}
        />
      }
      trailing={
        showFollowerBell ? (
          <DynamicIcon
            name="bell"
            library="lucide"
            size={12}
            sx={{ color: (theme) => theme.palette.custom.OV.extended.Signature.color }}
          />
        ) : undefined
      }
      hoverAction={
        hasOverlay
          ? {
              icon: <DynamicIcon name="more-vertical" library="lucide" size={11} />,
              label: OPTIONS_ACTION_LABEL,
              onClick: openMenu,
            }
          : undefined
      }
      aria-disabled={readOnly || undefined}
      onClick={hasAction ? handleClick : undefined}
      onMouseEnter={hasHoverCard ? openHoverCardSoon : undefined}
      onMouseLeave={hasHoverCard ? closeHoverCardSoon : undefined}
      onFocus={hasHoverCard ? openHoverCardSoon : undefined}
      onBlur={hasHoverCard ? closeHoverCardSoon : undefined}
      title={hasHoverCard ? undefined : resolvedTitle}
      aria-label={resolvedTitle}
      aria-haspopup={hasMenu ? 'menu' : undefined}
      aria-expanded={hasMenu ? menuOpen : undefined}
    />
  );

  return (
    <>
      {hasHoverCard ? (
        <ClickAwayListener onClickAway={closeHoverCardNow}>
          <Tooltip
            placement="bottom-start"
            open={hoverCardOpen}
            onClose={closeHoverCardNow}
            disableHoverListener
            disableFocusListener
            disableTouchListener
            nonInteractable={false}
            slotProps={{
              popper: {
                sx: { pointerEvents: 'auto' },
              },
              tooltip: {
                onMouseEnter: openHoverCardNow,
                onMouseLeave: closeHoverCardSoon,
                sx: BARE_TOOLTIP_SLOT_SX,
              },
            }}
            title={hoverCard}
          >
            {pillButton}
          </Tooltip>
        </ClickAwayListener>
      ) : (
        pillButton
      )}

      {hasMenu ? (
        <Menu
          anchorEl={pillRef.current}
          open={menuOpen}
          onClose={() => setMenuOpen(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        >
          {menuItems?.map((item) => (
            <MenuItem
              key={item.label}
              onClick={() => {
                item.onClick();
                setMenuOpen(false);
              }}
              sx={participantMenuItemSx}
            >
              <Stack
                direction="row"
                alignItems="center"
                className="participant-menu-item-content"
                sx={participantMenuItemContentSx}
              >
                {item.icon ? <ListItemIcon>{item.icon}</ListItemIcon> : null}
                <ListItemText>{item.label}</ListItemText>
              </Stack>
            </MenuItem>
          ))}
          {menuHelpText ? (
            <Typography variant="caption" sx={participantMenuHelpTextSx}>
              {menuHelpText}
            </Typography>
          ) : null}
        </Menu>
      ) : null}
    </>
  );
};
